import React, { useContext, useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { DatabaseContext } from '../../../redux/DatabaseStore';
import { useFormik } from 'formik';
import { getRequest, postRequest, putRequest } from '../../../api/ApiController';
import { SaveIcon, AddIcon, CopyIcon, VerticalLineIcon, SaveAndCloseIcon, FavoriteIcon, PriceIcon } from '../../svg/svgIcons';
import AuthContext from '../../../components/AuthContext';
import UtilContext from '../../../components/UtilContext';
import { LocaleContext } from '../../../components/LocaleContext';
import { Dropdown } from 'primereact/dropdown';
import { MultiSelect } from 'primereact/multiselect';
import { Calendar } from 'primereact/calendar';
import IntersectionObserverWrapper from '../../../components/toolbar/IntersectionObserverWrap';
import 'primereact/resources/themes/lara-light-indigo/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';

export default function ChecklistHolidayAdd({ holiday, type = 'add' }) {
  const [state, dispatch] = useContext(DatabaseContext);
  const { user } = useContext(AuthContext);
  const { toast, setDialogText, setVisible, openFavoriteDialog, setFastView } = useContext(UtilContext);
  const { locale, t } = useContext(LocaleContext);
  const [closeTab, setCloseTab] = useState(false);
  const submitRef = useRef(null);
  const insertCopyIcon = !user.permissions?.includes('edit settings');

  const primeCalendarLocale = useMemo(() => (locale === 'de' ? 'de' : locale === 'en' ? 'en' : 'sk'), [locale]);

  const SCOPE_OPTIONS = useMemo(
    () => [
      { label: t('ChecklistHolidayList.Scope.Departments'), value: 'DEPARTMENTS' },
      { label: t('ChecklistHolidayList.Scope.User'), value: 'USER' },
    ],
    [t]
  );

  const SELECTION_OPTIONS = useMemo(
    () => [
      { label: t('ChecklistHolidayAdd.SelectionMode.Include'), value: 'INCLUDE' },
      { label: t('ChecklistHolidayAdd.SelectionMode.AllExcept'), value: 'ALL_EXCEPT' },
    ],
    [t]
  );

  const usersWithEmployee = (state.users || []).filter((u) => u.employee);
  const userOptions = usersWithEmployee.map((u) => ({
    label: u.employee?.name || u.username || `#${u.id}`,
    value: u.id,
  }));

  const validate = useCallback(
    (values) => {
      const errors = {};
      if (!values.name?.trim()) errors.name = t('Common.RequiredField');
      if (!values.date_from) errors.date_from = t('Common.RequiredField');
      if (!values.date_to) errors.date_to = t('Common.RequiredField');
      if (values.date_from && values.date_to && new Date(values.date_from) > new Date(values.date_to)) {
        errors.date_to = t('ChecklistHolidayAdd.Error.DateToBeforeFrom');
      }
      if (!values.scope_type) errors.scope_type = t('Common.RequiredField');
      if (values.scope_type === 'DEPARTMENTS') {
        if (!values.selection_mode) errors.selection_mode = t('Common.RequiredField');
      }
      if (values.scope_type === 'USER') {
        if (!values.user_id) errors.user_id = t('ChecklistHolidayAdd.Error.UserRequired');
        if (values.user_id && values.substitute_user_id && values.user_id === values.substitute_user_id) {
          errors.substitute_user_id = t('ChecklistHolidayAdd.Error.SubstituteMustDiffer');
        }
      }
      return errors;
    },
    [t]
  );

  const formik = useFormik({
    initialValues: holiday
      ? {
          name: holiday.name,
          date_from: holiday.date_from ? new Date(holiday.date_from) : null,
          date_to: holiday.date_to ? new Date(holiday.date_to) : null,
          scope_type: holiday.scope_type || 'DEPARTMENTS',
          selection_mode: holiday.selection_mode || 'INCLUDE',
          department_ids: holiday.department_ids || [],
          user_ids: holiday.user_ids || [],
          user_id: holiday.user_id ?? null,
          substitute_user_id: holiday.substitute_user_id ?? null,
        }
      : {
          name: '',
          date_from: null,
          date_to: null,
          scope_type: 'DEPARTMENTS',
          selection_mode: 'INCLUDE',
          department_ids: [],
          user_ids: [],
          user_id: null,
          substitute_user_id: null,
        },
    enableReinitialize: true,
    validate,
    onSubmit: (values) => {
      setDialogText?.(t('Common.Saving'));
      setVisible?.(true);
      const formatDate = (d) => {
        if (!d) return null;
        const date = d instanceof Date ? d : new Date(d);
        const y = date.getFullYear();
        const m = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${y}-${m}-${day}`;
      };
      const payload = {
        name: values.name.trim(),
        date_from: formatDate(values.date_from),
        date_to: formatDate(values.date_to),
        scope_type: values.scope_type,
        selection_mode: values.scope_type === 'DEPARTMENTS' ? values.selection_mode : null,
        department_ids: values.scope_type === 'DEPARTMENTS' ? (values.department_ids || []) : null,
        user_ids: values.scope_type === 'DEPARTMENTS' ? (values.user_ids || []) : null,
        user_id: values.scope_type === 'USER' ? values.user_id : null,
        substitute_user_id: values.scope_type === 'USER' ? values.substitute_user_id || null : null,
      };

      if (type === 'add') {
        postRequest('/api/checklist-holidays', JSON.stringify(payload))
          .then(() => {
            getRequest('/api/checklist-holidays').then((res) => {
              const data = Array.isArray(res) ? res : res?.data ?? [];
              dispatch({ type: 'load_checklist_holidays', newChecklistHolidays: data });
            });
            if (closeTab) dispatch({ type: 'close_tab', id: state.selectedTab });
            setVisible?.(false);
            toast?.current?.show({
              severity: 'info',
              summary: t('ChecklistHolidayList.Toast.DeletedSummary'),
              life: 3000,
              detail: t('Common.Toast.RecordSavedDetail'),
            });
          })
          .catch((err) => {
            setVisible?.(false);
            const msg = err?.message || err?.data?.message || t('ChecklistHolidayAdd.Toast.SaveFailed');
            toast?.current?.show({ severity: 'warn', summary: t('Common.Error'), detail: msg, life: 4000 });
          });
      } else {
        putRequest(`/api/checklist-holidays/${holiday.id}`, JSON.stringify(payload))
          .then(() => {
            getRequest('/api/checklist-holidays').then((res) => {
              const data = Array.isArray(res) ? res : res?.data ?? [];
              dispatch({ type: 'load_checklist_holidays', newChecklistHolidays: data });
            });
            if (closeTab) dispatch({ type: 'close_tab', id: state.selectedTab });
            setVisible?.(false);
            toast?.current?.show({
              severity: 'info',
              summary: t('ChecklistHolidayList.Toast.DeletedSummary'),
              life: 3000,
              detail: t('Common.Toast.RecordUpdatedDetail'),
            });
          })
          .catch((err) => {
            setVisible?.(false);
            const msg = err?.message || err?.data?.message || t('ChecklistHolidayAdd.Toast.UpdateFailed');
            toast?.current?.show({ severity: 'warn', summary: t('Common.Error'), detail: msg, life: 4000 });
          });
      }
    },
  });

  const departmentIds = formik.values.department_ids || [];
  const usersFromSelectedDepartments = usersWithEmployee.filter((u) =>
    u.employee?.department_id != null && departmentIds.includes(u.employee.department_id)
  );
  const departmentUserOptions = usersFromSelectedDepartments.map((u) => ({
    label: u.employee?.name || u.username || `#${u.id}`,
    value: u.id,
  }));

  const addNew = () => {
    dispatch({
      type: 'create_new_tab',
      name: t('ChecklistHolidayList.Tab.New'), translationKey: 'ChecklistHolidayList.Tab.New', legacyLabel: t('ChecklistHolidayList.Tab.New'),
      page: 'ChecklistHolidayAdd',
      props: { holiday: null, type: 'add' },
      mask: 'chck014',
    });
  };

  useEffect(() => {
    if (type !== 'edit' || !holiday || holiday.scope_type !== 'DEPARTMENTS') return;
    const deptIds = holiday.department_ids || [];
    const savedUserIds = holiday.user_ids || [];
    if (deptIds.length > 0 && savedUserIds.length === 0 && (state.users || []).length > 0) {
      const userIdsFromDepts = (state.users || [])
        .filter((u) => u.employee && deptIds.includes(u.employee.department_id))
        .map((u) => u.id);
      if (userIdsFromDepts.length > 0) formik.setFieldValue('user_ids', userIdsFromDepts);
    }
  }, [type, holiday?.id, holiday?.scope_type, holiday?.department_ids, holiday?.user_ids, state.users?.length]);

  const copyRecord = () => {
    dispatch({
      type: 'create_new_tab',
      name: t('ChecklistHolidayList.Tab.New'), translationKey: 'ChecklistHolidayList.Tab.New', legacyLabel: t('ChecklistHolidayList.Tab.New'),
      page: 'ChecklistHolidayAdd',
      props: { holiday: holiday ? { ...holiday, id: undefined } : null, type: 'add' },
      mask: 'chck014',
    });
  };

  return (
    <>
      <div className="w-full h-10 flex flex-row bg-[#333]">
        <IntersectionObserverWrapper>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="saveandclose">
            <button
              type="button"
              className="disabled:opacity-25"
              disabled={insertCopyIcon}
              onClick={() => {
                setCloseTab(true);
                submitRef.current?.click();
              }}
            >
              <SaveAndCloseIcon />
            </button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="save">
            <button type="button" className="disabled:opacity-25" disabled={insertCopyIcon} onClick={() => submitRef.current?.click()}>
              <SaveIcon />
            </button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="add">
            <button type="button" className="disabled:opacity-25" disabled={insertCopyIcon} onClick={addNew}>
              <AddIcon />
            </button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="copy">
            <button type="button" className="disabled:opacity-25" disabled={insertCopyIcon} onClick={copyRecord}>
              <CopyIcon />
            </button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="vert">
            <VerticalLineIcon />
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="favorite">
            <button type="button" onClick={() => openFavoriteDialog?.(t('ChecklistHolidayList.Tab.New'), 'ChecklistHolidayAdd')}>
              <FavoriteIcon />
            </button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="fast">
            <button type="button" onClick={() => setFastView?.('ChecklistHolidayAdd', null, null)}>
              <PriceIcon />
            </button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="vert">
            <VerticalLineIcon />
          </div>
        </IntersectionObserverWrapper>
      </div>
      <div className="overflow-y-auto h-[calc(100vh-177px)]">
        <form onSubmit={formik.handleSubmit}>
          <div className="p-6 m-4 bg-gray-100 rounded-md shadow-md max-w-2xl">
            <h1 className="text-2xl font-semibold text-zinc-900 mb-4">
              {type === 'edit' ? t('ChecklistHolidayList.Tab.Edit') : t('ChecklistHolidayList.Tab.New')}
            </h1>
            {type === 'edit' && holiday?.id && (
              <label className="block text-sm font-semibold text-gray-600 mb-2">
                {t('Common.RecordIdLabel')}: {holiday.id}
              </label>
            )}

            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-800 mb-1">{t('ChecklistHolidayAdd.Label.Name')}</label>
              <input
                name="name"
                type="text"
                value={formik.values.name}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className="block w-full px-4 py-2 bg-white border rounded-md"
                placeholder={t('ChecklistHolidayAdd.PlaceholderName')}
              />
              {formik.touched.name && formik.errors.name && (
                <div className="text-red-600 text-sm mt-1">{formik.errors.name}</div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-semibold text-gray-800 mb-1">{t('ChecklistHolidayAdd.Label.DateFrom')}</label>
                <Calendar
                  value={formik.values.date_from}
                  onChange={(e) => formik.setFieldValue('date_from', e.value)}
                  dateFormat="dd.mm.yy"
                  locale={primeCalendarLocale}
                  className="w-full"
                  showIcon
                />
                {formik.touched.date_from && formik.errors.date_from && (
                  <div className="text-red-600 text-sm mt-1">{formik.errors.date_from}</div>
                )}
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-800 mb-1">{t('ChecklistHolidayAdd.Label.DateTo')}</label>
                <Calendar
                  value={formik.values.date_to}
                  onChange={(e) => formik.setFieldValue('date_to', e.value)}
                  dateFormat="dd.mm.yy"
                  locale={primeCalendarLocale}
                  className="w-full"
                  showIcon
                />
                {formik.touched.date_to && formik.errors.date_to && (
                  <div className="text-red-600 text-sm mt-1">{formik.errors.date_to}</div>
                )}
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-800 mb-1">{t('ChecklistHolidayList.Column.Scope')}</label>
              <Dropdown
                value={formik.values.scope_type}
                options={SCOPE_OPTIONS}
                onChange={(e) => {
                  formik.setFieldValue('scope_type', e.value);
                  formik.setFieldValue('selection_mode', e.value === 'DEPARTMENTS' ? 'INCLUDE' : null);
                  formik.setFieldValue('department_ids', []);
                  formik.setFieldValue('user_ids', []);
                  formik.setFieldValue('user_id', null);
                  formik.setFieldValue('substitute_user_id', null);
                }}
                placeholder={t('ChecklistHolidayAdd.PlaceholderScope')}
                className="w-full"
              />
              {formik.touched.scope_type && formik.errors.scope_type && (
                <div className="text-red-600 text-sm mt-1">{formik.errors.scope_type}</div>
              )}
            </div>

            {formik.values.scope_type === 'DEPARTMENTS' && (
              <>
                <div className="mb-4">
                  <label className="block text-sm font-semibold text-gray-800 mb-1">{t('ChecklistHolidayAdd.Label.SelectionMode')}</label>
                  <Dropdown
                    value={formik.values.selection_mode}
                    options={SELECTION_OPTIONS}
                    onChange={(e) => formik.setFieldValue('selection_mode', e.value)}
                    placeholder={t('ChecklistHolidayAdd.PlaceholderSelectionMode')}
                    className="w-full"
                  />
                  {formik.touched.selection_mode && formik.errors.selection_mode && (
                    <div className="text-red-600 text-sm mt-1">{formik.errors.selection_mode}</div>
                  )}
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-semibold text-gray-800 mb-1">
                    {formik.values.selection_mode === 'ALL_EXCEPT'
                      ? t('ChecklistHolidayAdd.Label.DepartmentsAllExcept')
                      : t('ChecklistHolidayAdd.Label.DepartmentsSelected')}
                  </label>
                  <MultiSelect
                    value={formik.values.department_ids}
                    options={state.departments || []}
                    onChange={(e) => {
                      const newDeptIds = e.value ?? [];
                      formik.setFieldValue('department_ids', newDeptIds);
                      const userIdsFromDepts = (state.users || [])
                        .filter((u) => u.employee && newDeptIds.includes(u.employee.department_id))
                        .map((u) => u.id);
                      formik.setFieldValue('user_ids', userIdsFromDepts);
                    }}
                    optionLabel="name"
                    optionValue="id"
                    placeholder={t('ChecklistHolidayAdd.PlaceholderDepartments')}
                    className="w-full"
                    filter
                    filterPlaceholder={t('ChecklistHolidayAdd.FilterDepartments')}
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-semibold text-gray-800 mb-1">
                    {t('ChecklistHolidayAdd.Label.UsersFromDepartments')}
                  </label>
                  <p className="text-xs text-gray-600 mb-1">{t('ChecklistHolidayAdd.HelpUsersFromDepartments')}</p>
                  <MultiSelect
                    value={formik.values.user_ids}
                    options={departmentUserOptions}
                    onChange={(e) => formik.setFieldValue('user_ids', e.value ?? [])}
                    optionLabel="label"
                    optionValue="value"
                    placeholder={
                      departmentIds.length
                        ? t('ChecklistHolidayAdd.PlaceholderUsers')
                        : t('ChecklistHolidayAdd.PlaceholderUsersNoDept')
                    }
                    className="w-full"
                    filter
                    filterPlaceholder={t('ChecklistHolidayAdd.FilterUser')}
                    disabled={departmentIds.length === 0}
                  />
                </div>
              </>
            )}

            {formik.values.scope_type === 'USER' && (
              <>
                <div className="mb-4">
                  <label className="block text-sm font-semibold text-gray-800 mb-1">{t('ChecklistHolidayAdd.Label.User')}</label>
                  <Dropdown
                    value={formik.values.user_id}
                    options={userOptions}
                    onChange={(e) => {
                      formik.setFieldValue('user_id', e.value);
                      if (e.value === formik.values.substitute_user_id) formik.setFieldValue('substitute_user_id', null);
                    }}
                    placeholder={t('ChecklistHolidayAdd.PlaceholderUser')}
                    className="w-full"
                    filter
                    filterPlaceholder={t('ChecklistHolidayAdd.FilterUser')}
                  />
                  {formik.touched.user_id && formik.errors.user_id && (
                    <div className="text-red-600 text-sm mt-1">{formik.errors.user_id}</div>
                  )}
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-semibold text-gray-800 mb-1">{t('ChecklistHolidayAdd.Label.Substitute')}</label>
                  <Dropdown
                    value={formik.values.substitute_user_id}
                    options={userOptions.filter((o) => o.value !== formik.values.user_id)}
                    onChange={(e) => formik.setFieldValue('substitute_user_id', e.value)}
                    placeholder={t('ChecklistHolidayAdd.PlaceholderSubstitute')}
                    className="w-full"
                    showClear
                    filter
                    filterPlaceholder={t('ChecklistHolidayAdd.FilterSubstitute')}
                  />
                  {formik.touched.substitute_user_id && formik.errors.substitute_user_id && (
                    <div className="text-red-600 text-sm mt-1">{formik.errors.substitute_user_id}</div>
                  )}
                </div>
              </>
            )}
          </div>
          <button type="submit" ref={submitRef} className="hidden" />
        </form>
      </div>
    </>
  );
}
